/**
 * 配信先 1 件分の行表示。複数メアド対応。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10.3
 */
import {
  Activity,
  BookOpen,
  CalendarCheck,
  FileText,
  Mail,
  MailOpen,
  MessageCircle,
  Pencil,
  Smartphone,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo, useState, type ComponentType, type MouseEvent } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useDeleteMailContact,
  usePromoteMailContact,
  useUpdateMailContact,
  type MailContactRecord,
} from '@/hooks/useMailContacts';
import {
  useContactTimeline,
  type ContactTimelineEvent,
} from '@/hooks/useContactTimeline';
import { formatDateTime } from '@/lib/firestoreTime';
import { formatDate } from '@/lib/firestoreTime';

import type {
  AudienceEventSource,
  AudienceEventType,
} from '@goenhub/shared';

import type { MailGroupRecord } from '@/hooks/useMailGroups';

interface Props {
  contact: MailContactRecord;
  /** id → group の lookup（行に表示するバッジ用） */
  groupMap?: Map<string, MailGroupRecord>;
  /** 編集ボタンクリック時のコールバック（親側で ContactEditDialog を開く） */
  onEdit?: (contact: MailContactRecord) => void;
}

export function ContactRow({ contact, groupMap, onEdit }: Props) {
  const deleteMutation = useDeleteMailContact();
  const updateMutation = useUpdateMailContact();
  const promoteMutation = usePromoteMailContact();
  const [expanded, setExpanded] = useState(false);

  const promotionLevel = contact.promotionLevel ?? 'lead';

  // 所属グループ（id → group オブジェクト解決、不明 ID は除外）
  const memberGroups = useMemo(() => {
    if (!groupMap) return [];
    return (contact.groupIds ?? [])
      .map((gid) => groupMap.get(gid))
      .filter((g): g is MailGroupRecord => !!g);
  }, [contact.groupIds, groupMap]);

  const handleDelete = async () => {
    if (!window.confirm(`「${contact.name || contact.defaultEmail}」を削除します。`)) return;
    try {
      await deleteMutation.mutateAsync(contact.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handlePromote = async (e: MouseEvent) => {
    e.stopPropagation();
    const label = contact.name || contact.defaultEmail;
    if (
      !window.confirm(
        `「${label}」を顧客に昇格します。\n顧客リスト（営業・取引）に表示されるようになります。`,
      )
    )
      return;
    try {
      await promoteMutation.mutateAsync({
        contactId: contact.id,
        to: 'customer',
        reason: '配信先管理から手動昇格',
      });
      toast.success('顧客に昇格しました');
    } catch (err) {
      toast.error(
        `昇格失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const handleSetDefault = async (address: string) => {
    if (address === contact.defaultEmail) return;
    try {
      await updateMutation.mutateAsync({
        id: contact.id,
        data: { defaultEmail: address },
      });
      toast.success(`既定配信先を ${address} に変更`);
    } catch (err) {
      toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const customFieldEntries = Object.entries(contact.customFields ?? {});
  const hasMultipleEmails = (contact.emails?.length ?? 0) > 1;

  // ─── ヘッダ表示用の構造化フィールド整形 (v3.3.x) ───
  const dash = '-';
  /** 姓 + 名 (両方なければ name にフォールバック) */
  const fullName = (() => {
    const ln = contact.lastName?.trim();
    const fn = contact.firstName?.trim();
    if (ln && fn) return `${ln} ${fn}`;
    if (ln) return ln;
    if (fn) return fn;
    return contact.name?.trim() || '';
  })();
  /** 姓（カナ）名（メイ） */
  const fullNameKana = (() => {
    const lk = contact.lastNameKana?.trim();
    const fk = contact.firstNameKana?.trim();
    if (lk && fk) return `${lk} ${fk}`;
    if (lk) return lk;
    if (fk) return fk;
    return '';
  })();
  const companyDisp = contact.companyName?.trim() || '';
  const genderDisp = contact.gender?.trim() || '';
  const occupationDisp = contact.occupation?.trim() || '';
  const phoneDisp = contact.phone?.trim() || '';
  const birthdateDisp = contact.birthdate?.trim() || '';
  const addressDisp = (() => {
    const a = contact.personalAddress;
    if (!a) return '';
    const parts = [a.postalCode ? `〒${a.postalCode}` : '', a.prefecture, a.city, a.line]
      .filter((x): x is string => !!x?.trim());
    return parts.join(' ');
  })();
  // 展開すると「複数メアド」「カスタムフィールド」「行動履歴（audience_events）」を表示。
  // 行動履歴はどの contact でも表示価値があるので常に展開可能とする。
  const hasDetails = true;
  // タイムラインは expanded=true のときだけ fetch
  const timelineQ = useContactTimeline({
    contactId: contact.id,
    lineUid: contact.lineUid ?? null,
    enabled: expanded,
    limit: 100,
  });

  return (
    <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] hover:border-[var(--text-muted)] transition-colors">
      <div className="flex items-center justify-between p-3 gap-3">
        <button
          type="button"
          onClick={() => hasDetails && setExpanded(!expanded)}
          className="flex-1 text-left flex items-center gap-3 min-w-0"
          disabled={!hasDetails}
        >
          <Mail className="size-4 text-[var(--text-muted)] flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-medium truncate">
                {fullName || '（名前なし）'}
              </span>
              {fullNameKana && (
                <span className="text-[10px] text-[var(--text-muted)]">
                  ({fullNameKana})
                </span>
              )}
              <span className="text-[11px] text-[var(--text-secondary)]">
                {companyDisp || dash}
              </span>
              <span className="text-[11px] text-[var(--text-secondary)]">
                {genderDisp || dash}
              </span>
              <span className="text-[11px] text-[var(--text-secondary)]">
                {occupationDisp || dash}
              </span>
              {hasMultipleEmails && (
                <Badge variant="accent">{contact.emails.length} 件のメアド</Badge>
              )}
              {contact.isTestContact === true && (
                <Badge variant="danger" title="テスト顧客: 商談の自動マージ対象外">
                  テスト
                </Badge>
              )}
              {memberGroups.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {memberGroups.map((g) => (
                    <span
                      key={g.id}
                      className={
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ' +
                        (g.type === 'ai_keyword'
                          ? 'bg-[var(--accent-purple-subtle)] text-[var(--accent-purple)]'
                          : 'bg-[var(--accent-subtle)] text-[var(--accent)]')
                      }
                      title={
                        g.type === 'ai_keyword' ? 'AI グループ' : '手動グループ'
                      }
                    >
                      {g.type === 'ai_keyword' ? (
                        <Sparkles className="size-2.5" />
                      ) : (
                        <Users className="size-2.5" />
                      )}
                      {g.name}
                    </span>
                  ))}
                </div>
              )}
              {(contact.tags?.length ?? 0) > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {contact.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[var(--bg-hover)] text-[var(--text-secondary)]"
                      title="タグ"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-[var(--text-muted)] truncate">
              {contact.defaultEmail}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {promotionLevel === 'lead' ? (
            <Button
              type="button"
              variant="success"
              size="sm"
              onClick={handlePromote}
              disabled={promoteMutation.isPending}
              title="この連絡先を顧客に昇格（顧客リストへ移動）"
            >
              <TrendingUp />
              顧客昇格
            </Button>
          ) : (
            <Badge variant={promotionLevel === 'inactive' ? 'default' : 'approved'}>
              {promotionLevel === 'inactive' ? '休眠' : '顧客'}
            </Badge>
          )}
          <span className="text-[10px] text-[var(--text-muted)]">
            {formatDate(contact.updatedAt)}
          </span>
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(contact);
              }}
              title="編集"
            >
              <Pencil className="size-3.5" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            title="削除"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="px-3 pb-3 space-y-3 border-t border-[var(--border)] pt-3">
          {/* 展開時の追加プロファイル情報 */}
          {(phoneDisp || addressDisp || genderDisp || birthdateDisp) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                プロファイル
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div className="flex gap-1 min-w-0">
                  <span className="text-[var(--text-muted)] flex-shrink-0">電話:</span>
                  <span className="truncate font-mono">{phoneDisp || dash}</span>
                </div>
                <div className="flex gap-1 min-w-0">
                  <span className="text-[var(--text-muted)] flex-shrink-0">性別:</span>
                  <span className="truncate">{genderDisp || dash}</span>
                </div>
                <div className="flex gap-1 min-w-0 col-span-2">
                  <span className="text-[var(--text-muted)] flex-shrink-0">住所:</span>
                  <span className="truncate">{addressDisp || dash}</span>
                </div>
                <div className="flex gap-1 min-w-0">
                  <span className="text-[var(--text-muted)] flex-shrink-0">誕生日:</span>
                  <span className="truncate tabular-nums">{birthdateDisp || dash}</span>
                </div>
              </div>
            </div>
          )}
          {hasMultipleEmails && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                登録メアド（★ = 既定配信先）
              </div>
              <div className="space-y-1">
                {contact.emails.map((e) => (
                  <button
                    key={e.address}
                    type="button"
                    onClick={() => handleSetDefault(e.address)}
                    className="flex items-center gap-2 text-xs w-full text-left hover:bg-[var(--bg-hover)] rounded px-2 py-1 transition-colors"
                    disabled={updateMutation.isPending}
                  >
                    <Star
                      className={
                        e.address === contact.defaultEmail
                          ? 'size-3 fill-[var(--accent)] text-[var(--accent)]'
                          : 'size-3 text-[var(--text-muted)]'
                      }
                    />
                    <span className="font-mono">{e.address}</span>
                    <span className="text-[10px] text-[var(--text-muted)] ml-auto">
                      {formatDate(e.addedAt)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {customFieldEntries.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                カスタムフィールド
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {customFieldEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-1 min-w-0">
                    <span className="text-[var(--text-muted)] flex-shrink-0">{k}:</span>
                    <span className="truncate">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ContactTimeline
            isLoading={timelineQ.isLoading}
            events={timelineQ.data ?? []}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 行動履歴タイムライン（audience_events）
// ─────────────────────────────────────────────
const TIMELINE_ICON: Record<
  AudienceEventType,
  ComponentType<{ className?: string }>
> = {
  mail_sent: Mail,
  mail_open: MailOpen,
  line_news_received: MessageCircle,
  form_submitted: FileText,
  landing_view: Smartphone,
  document_view: BookOpen,
};

const TIMELINE_LABEL: Record<AudienceEventType, string> = {
  mail_sent: 'メール送信',
  mail_open: 'メール開封',
  line_news_received: 'LINE 受信',
  form_submitted: 'フォーム送信',
  landing_view: 'LP 閲覧',
  document_view: '資料閲覧',
};

const SOURCE_LABEL: Record<AudienceEventSource, string> = {
  mail: 'メール経由',
  line: 'LINE 経由',
  landing: 'LP 経由',
  referral: 'タグ付き URL',
  direct: 'ダイレクト',
};

/**
 * 24h 以内 + 同一 campaignId のイベントを 1 フローとしてグルーピング。
 * 並び順は timestamp 降順を保ちつつ、隣接イベント間に「フロー継続」フラグを立てる。
 *  → 視覚的に左サイドにコネクタ縦線を表示し、メール送信→開封→LP 閲覧→フォーム送信の
 *    funnel を一目で追えるようにする。
 */
const FLOW_WINDOW_MS = 24 * 60 * 60 * 1000;

function annotateFlowChain(
  events: ContactTimelineEvent[],
): Array<ContactTimelineEvent & { connectsToNext: boolean }> {
  // events は timestamp 降順で渡される。
  // 「ev[i] が ev[i+1] (= 1 つ古い) と同一フロー継続か」を判定。
  const out: Array<ContactTimelineEvent & { connectsToNext: boolean }> = [];
  for (let i = 0; i < events.length; i++) {
    const cur = events[i];
    const next = events[i + 1];
    let connects = false;
    if (next) {
      const tCur = cur.timestamp?.toMillis?.() ?? 0;
      const tNext = next.timestamp?.toMillis?.() ?? 0;
      const sameCampaign =
        !!cur.campaignId &&
        !!next.campaignId &&
        cur.campaignId === next.campaignId;
      const within = tCur > 0 && tNext > 0 && tCur - tNext <= FLOW_WINDOW_MS;
      connects = sameCampaign && within;
    }
    out.push({ ...cur, connectsToNext: connects });
  }
  return out;
}

function ContactTimeline({
  isLoading,
  events,
}: {
  isLoading: boolean;
  events: ContactTimelineEvent[];
}) {
  const annotated = useMemo(() => annotateFlowChain(events), [events]);

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1 flex items-center gap-1">
        <Activity className="size-3" />
        行動履歴（audience_events）
        <span className="ml-2 normal-case tracking-normal text-[var(--text-muted)]">
          連続バー = 同一キャンペーンで 24h 以内に発生した一連のフロー
        </span>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner className="size-4" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-[11px] text-[var(--text-muted)] py-2">
          記録された行動はありません。
        </p>
      ) : (
        <ol className="space-y-0 max-h-72 overflow-y-auto pr-1">
          {annotated.map((ev, idx) => {
            const Icon = TIMELINE_ICON[ev.type] ?? CalendarCheck;
            const prevConnects =
              idx > 0 ? annotated[idx - 1].connectsToNext : false;
            return (
              <li
                key={ev.id}
                className="flex items-start gap-2 text-xs hover:bg-[var(--bg-hover)] rounded relative"
              >
                {/* 左側のフロー連続バー：上方向（より新しい側）と下方向（より古い側） */}
                <div className="flex flex-col items-center w-4 flex-shrink-0 self-stretch py-0.5">
                  <div
                    className={
                      'w-px flex-1 ' +
                      (prevConnects
                        ? 'bg-[var(--accent)]'
                        : 'bg-transparent')
                    }
                  />
                  <Icon className="size-3.5 text-[var(--text-muted)] my-0.5" />
                  <div
                    className={
                      'w-px flex-1 ' +
                      (ev.connectsToNext
                        ? 'bg-[var(--accent)]'
                        : 'bg-transparent')
                    }
                  />
                </div>
                <div className="flex-1 min-w-0 px-2 py-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{TIMELINE_LABEL[ev.type]}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-muted)]">
                      {SOURCE_LABEL[ev.source]}
                    </span>
                    {typeof ev.metadata?.refTag === 'string' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent-subtle)] text-[var(--accent)] font-mono">
                        ref: {ev.metadata.refTag}
                      </span>
                    )}
                    {ev.visitorId && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-muted)] font-mono"
                        title={`visitor ID: ${ev.visitorId}（localStorage 由来、匿名→顕名 backfill キー）`}
                      >
                        vid
                      </span>
                    )}
                    {ev.targetLabel && (
                      <span
                        className="truncate text-[var(--text-secondary)]"
                        title={ev.targetLabel}
                      >
                        {ev.targetLabel}
                      </span>
                    )}
                  </div>
                  {ev.campaignId && (
                    <div className="text-[10px] text-[var(--text-muted)] truncate">
                      campaign: {ev.campaignId}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0 mt-1.5 tabular-nums pr-2">
                  {formatDateTime(ev.timestamp)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
